import { useCallback } from 'react';
import { NavMarker } from 'react-rhumb';
import FormattedMessage from 'I18n/components/FormattedMessage';
import { FormattedIntlCurrency } from 'I18n/components/FormattedIntlCurrency';
import FormattedDateTime from 'I18n/components/FormattedDateTime';
import { Flex } from 'foundations-components/components/Flex';
import { StatusDot } from 'foundations-components/transitional/components/StatusDot';
import { EmptyState } from 'foundations-components/transitional/components/EmptyState';
import { Link } from 'foundations-components/transitional/components/Link';
import { CreditCardIcon } from 'foundations-assets/icons/components/CreditCardIcon';
import { BankIcon } from 'foundations-assets/icons/components/BankIcon';
import { SendIcon } from 'foundations-assets/icons/components/SendIcon';
import { PaginatedDataTable, DEFAULT_PAGE_SIZE } from '../components/PaginatedDataTable';
import { useCommercePaymentsData } from '../hooks/useCommercePaymentsData';
import { useCursorPagination } from '../hooks/useCursorPagination';
import { PAYMENT_METHOD_TYPE, commercePaymentStatusToStatusDotUse, commercePaymentStatusToMessage } from '../utils/typeAdapters';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
function AmountCell({
  row
}) {
  return /*#__PURE__*/_jsx("td", {
    children: /*#__PURE__*/_jsx(FormattedIntlCurrency, {
      value: Number(row.amount),
      currencyCode: row.currencyCode
    })
  });
}
function DateCell({
  row
}) {
  return /*#__PURE__*/_jsx("td", {
    children: typeof row.paymentDate === 'number' && /*#__PURE__*/_jsx(FormattedDateTime, {
      value: row.paymentDate,
      format: "ll"
    })
  });
}
function StatusCell({
  row
}) {
  const normalizedStatus = row.status.toUpperCase();
  const dotUse = normalizedStatus in commercePaymentStatusToStatusDotUse ? commercePaymentStatusToStatusDotUse[normalizedStatus] : undefined;
  const message = normalizedStatus in commercePaymentStatusToMessage ? commercePaymentStatusToMessage[normalizedStatus] : undefined;
  return /*#__PURE__*/_jsx("td", {
    children: /*#__PURE__*/_jsxs(Flex, {
      alignItems: "center",
      gap: "200",
      children: [dotUse && /*#__PURE__*/_jsx(StatusDot, {
        use: dotUse
      }), message && /*#__PURE__*/_jsx(FormattedMessage, {
        message: message
      })]
    })
  });
}
function PaymentMethodCell({
  row
}) {
  var _details$brand$toUppe, _details$brand, _details$last;
  const details = row.paymentMethodDetails;
  const isCard = details.type === PAYMENT_METHOD_TYPE.CARD;
  return /*#__PURE__*/_jsx("td", {
    children: /*#__PURE__*/_jsxs(Flex, {
      alignItems: "center",
      gap: "200",
      children: [isCard ? /*#__PURE__*/_jsx(CreditCardIcon, {
        size: "100"
      }) : /*#__PURE__*/_jsx(BankIcon, {
        size: "100"
      }), /*#__PURE__*/_jsx(FormattedMessage, {
        message: isCard ? 'paymentHistory.paymentMethod.card' : 'paymentHistory.paymentMethod.bank',
        options: {
          brand: (_details$brand$toUppe = (_details$brand = details.brand) === null || _details$brand === void 0 ? void 0 : _details$brand.toUpperCase()) !== null && _details$brand$toUppe !== void 0 ? _details$brand$toUppe : '',
          last4: (_details$last = details.last4) !== null && _details$last !== void 0 ? _details$last : ''
        }
      })]
    })
  });
}
function ActionsCell() {
  // TODO: handle navigation event when link is provided from BE
  const handleSendReceipt = useCallback(e => {
    e.preventDefault();
  }, []);
  return /*#__PURE__*/_jsx("td", {
    children: /*#__PURE__*/_jsx(Link, {
      href: "#",
      disabled: true,
      onClick: handleSendReceipt,
      children: /*#__PURE__*/_jsxs(Flex, {
        alignItems: "center",
        gap: "100",
        children: [/*#__PURE__*/_jsx(SendIcon, {
          size: "100"
        }), /*#__PURE__*/_jsx(FormattedMessage, {
          message: "paymentHistory.sendReceipt"
        })]
      })
    })
  });
}
const COLUMNS = [{
  columnName: 'amount',
  sortable: true,
  HeaderContentComponent: () => /*#__PURE__*/_jsx(FormattedMessage, {
    message: "paymentHistory.columns.amount"
  }),
  CellComponent: AmountCell
}, {
  columnName: 'paymentDate',
  sortable: true,
  HeaderContentComponent: () => /*#__PURE__*/_jsx(FormattedMessage, {
    message: "paymentHistory.columns.date"
  }),
  CellComponent: DateCell
}, {
  columnName: 'status',
  sortable: true,
  HeaderContentComponent: () => /*#__PURE__*/_jsx(FormattedMessage, {
    message: "paymentHistory.columns.status"
  }),
  CellComponent: StatusCell
}, {
  columnName: 'paymentMethodDetails',
  sortable: true,
  HeaderContentComponent: () => /*#__PURE__*/_jsx(FormattedMessage, {
    message: "paymentHistory.columns.paymentMethod"
  }),
  CellComponent: PaymentMethodCell
}, {
  columnName: 'actions',
  sortable: false,
  HeaderContentComponent: () => /*#__PURE__*/_jsx(FormattedMessage, {
    message: "paymentHistory.columns.actions"
  }),
  CellComponent: ActionsCell
}];
export function PaymentHistory() {
  const {
    currentOffset,
    currentPage,
    hasPrev,
    handleNextPage,
    handlePrevPage
  } = useCursorPagination();
  const {
    loading: paymentsLoading,
    data: paymentsData
  } = useCommercePaymentsData(Object.assign({
    pageLength: DEFAULT_PAGE_SIZE
  }, currentOffset !== undefined && {
    offset: currentOffset
  }));
  const hasMore = Boolean(paymentsData === null || paymentsData === void 0 ? void 0 : paymentsData.hasMore);
  const nextOffset = paymentsData === null || paymentsData === void 0 ? void 0 : paymentsData.offset;
  if ((paymentsData === null || paymentsData === void 0 ? void 0 : paymentsData.results.length) === 0 && !paymentsLoading) {
    return /*#__PURE__*/_jsx(EmptyState, {
      flush: true,
      layout: "vertical",
      primaryContent: /*#__PURE__*/_jsx(FormattedMessage, {
        message: "paymentHistory.emptyStateMessage"
      })
    });
  }
  return /*#__PURE__*/_jsx(NavMarker, {
    name: "PAYMENT_HISTORY_LOAD",
    children: /*#__PURE__*/_jsx("div", {
      "data-test-id": "payment-history-table",
      children: /*#__PURE__*/_jsx(PaginatedDataTable, {
        columns: COLUMNS,
        rows: (paymentsData === null || paymentsData === void 0 ? void 0 : paymentsData.results) || [],
        getRowId: row => {
          var _row$objectId;
          return String((_row$objectId = row.objectId) !== null && _row$objectId !== void 0 ? _row$objectId : row.paymentId);
        },
        loading: paymentsLoading,
        currentPage: currentPage,
        hasMore: hasMore,
        hasPrev: hasPrev,
        onNextPage: () => nextOffset && handleNextPage(nextOffset),
        onPrevPage: handlePrevPage
      })
    })
  });
}